import {
  ErrorBoundaries,
  EzDrawer,
  EzDrawerProvider,
  GlobalErrorHandler,
  NetworkDetector,
  SplashScreen,
} from '@biznessforce/ui-lib';
import {
  App as AntdApp,
  Button,
  ConfigProvider,
  message,
  notification,
  theme as antdTheme,
} from 'antd';
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React, { useEffect, useRef } from 'react';
import { Provider, connect } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { PersistGate } from 'redux-persist/integration/react';
import Routes from './routes/routes';
import { requestUser } from './store/auth/auth.actions';
import { updateGlobalError } from './store/config/config.actions';
import { InfoCircleFilled } from '@ant-design/icons';

dayjs.extend(utc);
dayjs.extend(timezonePlugin);

const BUILD_INFO_CHECK_INTERVAL_MS = 5 * 60 * 1000;

type Props = {
  store: any;
  persistor: any;
  basename?: string;
};

type AppContentProps = {
  basename?: string;
  requestUser: () => any;
  updateGlobalError: (v: any) => any;
  globalError: any;
  ajaxLoader: { isLoading: boolean };
};

type BuildInfo = {
  buildId?: string;
};

const getBuildInfoUrl = (basename?: string) => {
  const basePath = basename && basename !== '/' ? `/${basename.replace(/^\/|\/$/g, '')}` : '';
  return `${basePath}/build-info.json?t=${Date.now()}`;
};

function AppContent({
  basename,
  requestUser,
  globalError,
  ajaxLoader,
  updateGlobalError,
}: AppContentProps) {
  const { isLoading } = ajaxLoader || { isLoading: false };
  const [messageApi, contextHolder] = message.useMessage();
  const [notificationApi, notificationContextHolder] = notification.useNotification();
  const notifiedBuildIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (isLoading) {
      messageApi.open({ type: 'loading', content: 'Loading...', duration: 0 });
    } else {
      messageApi.destroy();
    }
  }, [isLoading, messageApi]);

  useEffect(() => {
    requestUser();
  }, [requestUser]);

  useEffect(() => {
    const checkForUpdate = async () => {
      try {
        const response = await fetch(getBuildInfoUrl(basename), {
          cache: 'no-store',
        });

        if (!response.ok) {
          return;
        }

        const buildInfo = (await response.json()) as BuildInfo;
        const remoteBuildId = buildInfo.buildId;

        if (
          !remoteBuildId ||
          remoteBuildId === __APP_BUILD_ID__ ||
          notifiedBuildIdRef.current === remoteBuildId
        ) {
          return;
        }

        notifiedBuildIdRef.current = remoteBuildId;
        notificationApi.open({
          key: 'new-build-available',
          icon: <InfoCircleFilled />,
          message: 'New update available',
          description: 'Refresh the page to load the latest version.',
          style: { padding: '12px' },
          duration: 0,
          btn: (
            <Button size="small" type="primary" onClick={() => window.location.reload()}>
              Refresh
            </Button>
          ),
        });
      } catch {
        // Update checks must never interrupt normal app usage.
      }
    };

    checkForUpdate();
    const intervalId = window.setInterval(checkForUpdate, BUILD_INFO_CHECK_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [basename, notificationApi]);

  return (
    <React.Suspense fallback={<SplashScreen />}>
      <BrowserRouter basename={basename}>
        <ConfigProvider
          theme={{
            algorithm: antdTheme.defaultAlgorithm,
            token: {
              colorText: '#12344d',
              colorPrimary: '#12344d',
            },
            components: {
              Button: {
                colorPrimary: '#12344d',
                algorithm: true,
              },
            },
          }}
        >
          <AntdApp>
            {contextHolder}
            {notificationContextHolder}
            <NetworkDetector>
              <GlobalErrorHandler
                globalError={globalError}
                updateGlobalError={(v) => updateGlobalError(v)}
              >
                <ErrorBoundaries>
                  <EzDrawerProvider>
                    <Routes />
                    <EzDrawer />
                  </EzDrawerProvider>
                </ErrorBoundaries>
              </GlobalErrorHandler>
            </NetworkDetector>
          </AntdApp>
        </ConfigProvider>
      </BrowserRouter>
    </React.Suspense>
  );
}

// Connect AppContent to Redux
const ConnectedAppContent = connect(
  (state: any) => ({
    globalError: Object(state.config?.globalError),
    ajaxLoader: Object(state.config?.ajaxLoader || {}),
  }),
  {
    requestUser,
    updateGlobalError,
  }
)(AppContent);

export function App({ store, persistor, basename }: Props) {
  return (
    <Provider store={store}>
      <PersistGate persistor={persistor} loading={<SplashScreen />}>
        <ConnectedAppContent basename={basename} />
      </PersistGate>
    </Provider>
  );
}

export default App;
